/* eslint-disable camelcase */
import { DateTime } from "luxon";
import { Tabulator, FormatModule, PageModule, SortModule, FrozenColumnsModule, InteractionModule } from "tabulator-tables";
import type { ColumnDefinition } from "tabulator-tables";

import type { Epicenter } from "../../../api/epicenter/structs";
import { eqLocationTypeIdFormatter, originTimeFormatter, stringCellFormatter } from "../../../common/cellformatter";
import { pageUrls } from "../../../urls";

// 必要なモジュールを登録
Tabulator.registerModule([FormatModule, FrozenColumnsModule, PageModule, SortModule, InteractionModule]);

/**
 * 震源検索→表エリア
 */
export class ResultArea {
    /** テーブルのルート要素 */
    #tableElem: HTMLElement;
    /** Tabulatorインスタンス */
    #table?: Tabulator;

    /**
     * コンストラクター。
     *
     * @param elem 表エリアのルート要素
     */
    constructor(elem: HTMLElement) {
        this.#tableElem = elem;
    }

    /**
     * 初期化処理を行う。
     * Tabulatorテーブルを構築し、行クリックイベントを設定する。
     */
    async init() {
        const table = new Tabulator(this.#tableElem, {
            dependencies: {
                DateTime,
            },
            minHeight: 400,
            layout: "fitDataTable",
            pagination: true,
            paginationSize: 20,
            paginationSizeSelector: [10, 20, 50, 100],
            paginationCounter: (pageSize: number, currentRow: number, _currentPage: number, totalRows: number) => {
                if (totalRows === 0) {
                    return "";
                }
                const startRow = currentRow;
                const endRow = Math.min(currentRow + pageSize - 1, totalRows);
                return `${startRow}-${endRow} 件目 / 全 ${totalRows} 件`;
            },
            columns: [
                { title: "震源ID", field: "eqSourceId", sorter: "number", frozen: true, minWidth: 100 },
                {
                    title: "発生時刻",
                    field: "jemOriginTime",
                    sorter: "datetime",
                    sorterParams: { format: "iso" },
                    formatter: originTimeFormatter,
                },
                { title: "緯度", field: "jemLatStr", sorter: "number", hozAlign: "right", formatter: stringCellFormatter },
                { title: "経度", field: "jemLonStr", sorter: "number", hozAlign: "right", formatter: stringCellFormatter },
                { title: "深さ [km]", field: "jemDepthStr", sorter: "number", hozAlign: "right", formatter: stringCellFormatter },
                { title: "Mj", field: "mjmaStr", sorter: "number", hozAlign: "right", formatter: stringCellFormatter },
                { title: "Mw", field: "mwStr", sorter: "number", hozAlign: "right", formatter: stringCellFormatter },
                { title: "地震発生位置種別", field: "eqLocationTypeId", sorter: "number", formatter: eqLocationTypeIdFormatter },
                { title: "波形データ数", field: "dataCount", sorter: "number", hozAlign: "right" },
            ] as ColumnDefinition[],
            locale: "ja-jp",
            langs: {
                "ja-jp": {
                    pagination: {
                        page_size: "ページサイズ",
                        first: "«",
                        first_title: "最初のページ",
                        last: "»",
                        last_title: "最後のページ",
                        prev: "‹",
                        prev_title: "前のページ",
                        next: "›",
                        next_title: "次のページ",
                    },
                },
            },
        });

        table.on("rowClick", (_, row) => {
            const eqSourceId = row.getData().eqSourceId;

            if (typeof eqSourceId === "string") {
                const url = pageUrls["/event/[eqSourceId]"].get({ params: { eqSourceId } });
                window.open(url, "_blank", "noreferrer");
            }
        });

        this.#table = table;
    }

    /**
     * 震源データをテーブルに表示する。
     *
     * @param epicenters 表示する震源データの配列
     */
    async setEpicenters(epicenters: Epicenter[] | undefined) {
        const table = this.#table;
        if (!table) {
            console.warn("Table not initialized");
            return;
        }

        if (epicenters) {
            // データ設定後にカラム幅を自動調整
            await table.setData(epicenters);
            table.redraw(true);
        } else {
            table.clearData();
        }
    }
}
